import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { BidTypeEnum } from "../../../common/commonEnums";
import { IBid, IListing } from "../../../common/commonTypes";
import Avatar from "../../../common/components/Avatar";
import Card from "../../../common/components/Card/Card";
import { Truncate } from "../../../common/components/styled";
import {
  Paragraph14Bold,
  Paragraph14Regular,
} from "../../../common/Typography/Paragraph14";
import { Paragraph16Bold } from "../../../common/Typography/Paragraph16";
import {} from "../../../helpers/format";
import { routes } from "../../../routes";
import { formatDate, formatTime } from "../../../helpers/dateTime";
import StaticLink from "../../../common/components/StaticLink/StaticLink";
import { ethers } from "ethers";
import { useCoingeckoPrice } from "../../../queries/coingecko.query";
import { tokenToUsdPriceLocaleString } from "../../../helpers/tokenHelper";

const HistoryItem = styled(Card)`
  display: flex;
  flex-direction: row;
  margin-bottom: 20px;
  padding: 20px;
  position: relative;
  align-items: center;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    align-items: flex-start;
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
`;

const LeftContainer = styled.div`
  max-width: 304px;

  @media (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
    max-width: 204px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    max-width: 204px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileS}) {
    max-width: 104px;
  }
`;

const Description = styled(Paragraph14Bold)``;

const TimestampDesktop = styled(Paragraph14Regular)`
  color: ${(props) => props.theme.greyColor};
  display: block;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    display: none;
  }
`;

const TimestampMobile = styled(Paragraph14Regular)`
  color: ${(props) => props.theme.greyColor};
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    display: block;
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Prices = styled.div`
  margin-left: 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  white-space: nowrap;
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    align-items: center;
    flex-direction: row;
  }
`;

const PriceEth = styled(Paragraph16Bold)``;

const PriceUsd = styled(Paragraph14Bold)`
  color: ${(props) => props.theme.greyColor};
`;

const Content = styled.div`
  display: flex;
  justify-content: space-between;
  flex-grow: 1;
  margin-left: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    flex-direction: column;
  }
`;

const EmptyLabel = styled(Paragraph14Bold)`
  color: ${(props) => props.theme.greyColor};
`;

interface IProps {
  bids?: IBid[];
  listing: IListing;
}

const Timeline = ({ bids = [], listing }: IProps) => {
  const { prices } = useCoingeckoPrice(listing.asset);
  const { t } = useTranslation();

  const getBidDescription = (bid: IBid): string => {
    const translationKey =
      listing?.type === BidTypeEnum.fixed ? "bought" : "bid-placed";

    if (bid.user?.username) {
      return t(`${translationKey}-by`, {
        user: bid.user.username,
      });
    }

    return t(translationKey);
  };

  const getBidTimestamp = (bid: IBid): string => {
    if (bid.created) {
      const time = formatTime(bid.created);
      const date = formatDate(bid.created);

      return `${time} - ${date}`;
    }

    return "";
  };

  return (
    <div>
      {bids.length === 0 && (
        <Card>
          <EmptyLabel>{t("timeline-empty-message")}</EmptyLabel>
        </Card>
      )}
      {bids.map((bid) => (
        <HistoryItem key={bid.id}>
          <StaticLink href={routes.user.url(bid.user!.uri)}>
            <Avatar user={bid.user} />
          </StaticLink>
          <Content>
            <Left>
              <LeftContainer>
                <Truncate>
                  <Description>{getBidDescription(bid)}</Description>
                </Truncate>

                <TimestampDesktop>{getBidTimestamp(bid)}</TimestampDesktop>
              </LeftContainer>
            </Left>
            <Right>
              <Prices>
                <PriceEth>
                  {Number(
                    ethers.utils.formatUnits(
                      bid.amount,
                      bid.listing.asset.decimals
                    )
                  ).toLocaleString()}
                  {bid.listing.asset.symbol}
                </PriceEth>
                <PriceUsd>
                  ${" "}
                  {tokenToUsdPriceLocaleString(
                    ethers.utils.formatUnits(
                      bid.amount,
                      bid.listing.asset.decimals
                    ),
                    prices[listing.asset.coingecko_id]?.usd
                  )}
                </PriceUsd>
              </Prices>
              <TimestampMobile>{getBidTimestamp(bid)}</TimestampMobile>
            </Right>
          </Content>
          {/* TODO: add link to tx */}
          {/*{bid.txHash && <Icon name="out" size="20px" />}*/}
        </HistoryItem>
      ))}
    </div>
  );
};

export default Timeline;
